import { useEditor } from "@tiptap/react";
import { useEffect } from "react";
import type { TDocumentContent } from "@shared/entities/documentTemplate";
import type { TDocumentVariableKey } from "@shared/entities/documentVariables";
import type { DocumentEditorProps, EditorAdapterHandle } from "../../types";
import { substituteVariables } from "./substitute";
import { buildEditorExtensions } from "./extensions";

export function useTipTapDocumentEditor({
  content,
  onChange,
  readOnly,
  placeholder,
  onReady,
}: Pick<DocumentEditorProps, "content" | "onChange" | "readOnly" | "placeholder" | "onReady">) {
  const editor = useEditor({
    extensions: buildEditorExtensions({ placeholder }),
    content: content ?? undefined,
    editable: !readOnly,
    editorProps: {
      attributes: {
        dir: "rtl",
        class: "prose prose-sm max-w-none focus:outline-none min-h-[200px] p-3",
      },
    },
    onUpdate({ editor: e }) {
      onChange?.(e.getJSON() as TDocumentContent);
    },
  });

  // Sync external content changes (e.g. template load)
  useEffect(() => {
    if (!editor || content === null) return;
    const current = JSON.stringify(editor.getJSON());
    const next = JSON.stringify(content);
    if (current !== next) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  // Sync readOnly changes after mount
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  // Expose handle via onReady
  useEffect(() => {
    if (!editor || !onReady) return;

    const handle: EditorAdapterHandle = {
      insertVariable(key: TDocumentVariableKey) {
        editor.commands.insertContent({ type: "variable", attrs: { key } });
      },
      substituteVariables(values: Partial<Record<TDocumentVariableKey, string>>) {
        const current = editor.getJSON() as TDocumentContent;
        const result = substituteVariables(current, values);
        editor.commands.setContent(result);
      },
      getContent() {
        return editor.getJSON() as TDocumentContent;
      },
      setContent(c: TDocumentContent) {
        editor.commands.setContent(c);
      },
      focus() {
        editor.commands.focus();
      },
    };

    onReady(handle);
    // onReady is intentionally called once when editor mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  return { editor };
}
